import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { Modal, Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import useApi from '../../hooks/useApi'
import Loading from '../loading'
import FollowButton from '../../pages/events/VolunteerList/FollowButton'
import UserRow from './UserRow'
import { followingUserIdSetFromPayload } from '../../lib/following'
import styles from './FollowersListModal.module.scss'

function parseFollowersPayload(res) {
  if (!res || (res.status != null && (res.status < 200 || res.status >= 300)))
    return []
  const d = res.data
  if (Array.isArray(d)) return d
  if (Array.isArray(d?.followers)) return d.followers
  return []
}

export default function FollowersListModal({
  show,
  onHide,
  profileUserId,
  fetchFollowers,
  followersTotal = 0,
  currentUserId = null,
}) {
  const { t } = useTranslation()
  const { getUserFollowers, getUserFollowing } = useApi()

  const [followers, setFollowers] = useState([])
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [followingIds, setFollowingIds] = useState(() => new Set())
  const prevShowRef = useRef(false)

  const subjectId = profileUserId

  const refreshFollowingIds = useCallback(async () => {
    if (!currentUserId) {
      setFollowingIds(new Set())
      return
    }
    try {
      const res = await getUserFollowing({ id: currentUserId })
      if (res?.data && Array.isArray(res.data)) {
        setFollowingIds(followingUserIdSetFromPayload(res.data))
      }
    } catch {
      /* ignore */
    }
  }, [currentUserId, getUserFollowing])

  const loadFollowersPage = useCallback(
    pageNum => {
      if (typeof fetchFollowers === 'function') {
        return fetchFollowers({ page: pageNum })
      }
      if (subjectId == null) return Promise.resolve(null)
      return getUserFollowers({ id: subjectId, page: pageNum })
    },
    [fetchFollowers, getUserFollowers, subjectId],
  )

  const fetchPage = useCallback(
    async (nextPage, { append } = { append: false }) => {
      if (subjectId == null && typeof fetchFollowers !== 'function') return
      setLoading(true)
      setLoadError(false)
      try {
        const [followersRes] = await Promise.all([
          loadFollowersPage(nextPage),
          nextPage === 1 && currentUserId
            ? refreshFollowingIds()
            : Promise.resolve(),
        ])
        const batch = parseFollowersPayload(followersRes)
        if (append) {
          setFollowers(prev => {
            const seen = new Set(prev.map(u => u.id))
            const merged = [...prev]
            batch.forEach(u => {
              if (u?.id != null && !seen.has(u.id)) {
                seen.add(u.id)
                merged.push(u)
              }
            })
            return merged
          })
        } else {
          setFollowers(batch)
        }
        setPage(nextPage)
        const failed =
          followersRes?.status != null &&
          (followersRes.status < 200 || followersRes.status >= 300)
        if (failed && !append) setLoadError(true)
        if (!failed) setLoadError(false)
      } catch {
        if (!append) {
          setLoadError(true)
          setFollowers([])
        }
      } finally {
        setLoading(false)
      }
    },
    [
      subjectId,
      fetchFollowers,
      currentUserId,
      loadFollowersPage,
      refreshFollowingIds,
    ],
  )

  useEffect(() => {
    const wasOpen = prevShowRef.current
    const canFetch = subjectId != null || typeof fetchFollowers === 'function'
    if (show && !wasOpen && canFetch) {
      setFollowers([])
      setPage(0)
      setLoadError(false)
      fetchPage(1, { append: false })
    }
    if (!show && wasOpen) {
      setFollowers([])
      setPage(0)
      setLoadError(false)
    }
    prevShowRef.current = show
  }, [show, subjectId, fetchFollowers, fetchPage])

  const enrichedFollowers = useMemo(
    () =>
      followers.map(u => ({
        ...u,
        current_user_follows: u.current_user_follows ?? followingIds.has(u.id),
      })),
    [followers, followingIds],
  )

  const hasMore = useMemo(() => {
    if (followersTotal <= 0) return false
    return enrichedFollowers.length < followersTotal
  }, [enrichedFollowers.length, followersTotal])

  const handleLoadMore = useCallback(() => {
    if (!hasMore || loading) return
    fetchPage(page + 1, { append: true })
  }, [hasMore, loading, page, fetchPage])

  const handleFollowUpdate = useCallback(async () => {
    await refreshFollowingIds()
  }, [refreshFollowingIds])

  const title = t('profiles.followersListTitle', 'Followers')

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.body}>
        <Loading show={loading && enrichedFollowers.length === 0} />
        {!loading && enrichedFollowers.length === 0 && loadError && (
          <p className={styles.muted}>
            {t('profiles.followersListError', 'Could not load followers.')}
          </p>
        )}
        {!loading && enrichedFollowers.length === 0 && !loadError && (
          <p className={styles.muted}>
            {t('profiles.followersListEmpty', 'No followers yet.')}
          </p>
        )}
        {enrichedFollowers.length > 0 && (
          <ul className={styles.list}>
            {enrichedFollowers.map(user => (
              <li key={user.id} className={styles.listItem}>
                <UserRow user={user}>
                  {currentUserId &&
                  user.id !== currentUserId &&
                  !user.current_user_blocks ? (
                    <FollowButton
                      volunteer={{ id: user.id, user }}
                      isCurrentUser={false}
                      onUpdate={handleFollowUpdate}
                    />
                  ) : null}
                </UserRow>
              </li>
            ))}
          </ul>
        )}
        {hasMore && enrichedFollowers.length > 0 && (
          <div className={styles.footerActions}>
            <Button
              variant="outline-secondary"
              type="button"
              onClick={handleLoadMore}
              disabled={loading}
            >
              {loading
                ? t('profiles.followersListLoading', 'Loading…')
                : t('profiles.followersListLoadMore', 'Load more')}
            </Button>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" type="button" onClick={onHide}>
          {t('buttons.close', 'Close')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

FollowersListModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  profileUserId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fetchFollowers: PropTypes.func,
  followersTotal: PropTypes.number,
  currentUserId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}
